import React, { useRef } from 'react';
import { useContent } from '../context/ContentContext';
import { motion, useScroll, useTransform, Variants } from 'framer-motion';
import { Award, BookOpen, Star } from 'lucide-react';

const Team: React.FC = () => {
  const { content } = useContent();
  const members = content.team?.members || [];
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.8, 
        ease: [0.21, 1, 0.36, 1] as [number, number, number, number]
      }
    }
  };

  return (
    <section id="team" ref={sectionRef} className="py-24 bg-white relative overflow-hidden">
      {/* 视差背景图 */}
      <motion.div 
        style={{ y: backgroundY }}
        className="absolute inset-0 z-0 pointer-events-none"
      >
        <img 
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1920" 
          alt="Team Background" 
          className="w-full h-[120%] object-cover opacity-[0.03] grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white"></div>
      </motion.div>

      {/* 背景装饰：淡淡的品牌字影 */}
      <div className="absolute top-10 left-10 text-[10vw] font-black text-slate-50 select-none pointer-events-none -z-10 leading-none opacity-50">
        EXPERTS
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Star className="text-[#D4AF37] fill-[#D4AF37]" size={16} />
            <p className="text-[#D4AF37] tracking-[0.4em] font-black text-xs uppercase">Elite Professional Team</p>
            <Star className="text-[#D4AF37] fill-[#D4AF37]" size={16} />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">专家团队</h2>
          <div className="w-20 h-1.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto rounded-full"></div>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-8 text-gray-500 max-w-3xl mx-auto text-lg leading-relaxed"
          >
            汇聚注册会计师、注册税务师、律师等跨领域精英，平均从业经验超过10年，为您提供最权威的专业支持。
          </motion.p>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10"
        >
          {members.map((member, index) => (
            <motion.div 
              key={index} 
              variants={cardVariants}
              whileHover={{ y: -12 }}
              className="bg-white rounded-3xl overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.04)] border border-slate-100 hover:shadow-[0_20px_60px_rgba(212,175,55,0.15)] hover:border-[#D4AF37]/30 transition-all duration-500 group"
            >
              {/* 头像区域 */}
              <div className="h-72 overflow-hidden relative">
                <motion.img 
                  initial={{ scale: 1.1 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 1.5 }}
                  src={member.image} 
                  alt={`${member.name} - ${member.title}`} 
                  loading="lazy"
                  className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity"></div>
                <div className="absolute bottom-0 left-0 p-8 text-white w-full">
                  <motion.h3 
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="text-2xl font-bold mb-1 tracking-tight"
                  >
                    {member.name}
                  </motion.h3>
                  <p className="text-[#D4AF37] text-xs font-black uppercase tracking-[0.2em]">{member.title}</p>
                </div>
              </div>
              
              <div className="p-8">
                {/* 从业资质 */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3 text-slate-800 font-bold text-xs uppercase tracking-wider">
                    <Award size={14} className="text-[#D4AF37]" />
                    <span>核心资质</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {member.certifications.map((cert, idx) => (
                      <motion.span 
                        key={idx}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 + idx * 0.05 }}
                        className="bg-slate-50 border border-slate-200 text-slate-600 text-[10px] px-2.5 py-1 rounded-lg font-bold group-hover:bg-[#D4AF37]/10 group-hover:text-[#D4AF37] group-hover:border-[#D4AF37]/20 transition-colors"
                      >
                        {cert}
                      </motion.span>
                    ))}
                  </div>
                </div>

                {/* 擅长领域 */}
                <div className="mb-8">
                   <div className="flex items-center gap-2 mb-3 text-slate-800 font-bold text-xs uppercase tracking-wider">
                    <BookOpen size={14} className="text-[#D4AF37]" />
                    <span>深度专长</span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed font-medium group-hover:text-slate-700 transition-colors">
                    {member.specialties.join("、")}
                  </p>
                </div>
                
                {/* 底部经验栏 */}
                <div className="pt-6 border-t border-slate-100 flex justify-between items-center">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Industry Exp</span>
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse"></span>
                    <span className="text-slate-900 font-black text-sm">{member.experience}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Team;